import { FC, useCallback, useEffect, useState } from 'react';

import { Popover } from '@headlessui/react';
import { FaCompressAlt, FaExpandAlt } from 'react-icons/fa';
import { VscChevronDown } from 'react-icons/vsc';
import { COMPOSER_MODES, COMPOSER_TYPES } from 'utils/constants';
import { cn, useAdmin } from 'utils/helpers';
import { cva } from 'class-variance-authority';
import { useTasksContext } from 'modules/account/sales/containers/Tasks';
import { useGetTask, useUpdateTaskStatus } from 'modules/account/sales/hooks';
import { MdClose } from 'react-icons/md';
import MarkAsComplete from 'modules/account/sales/components/MarkAsComplete';

interface PROPS {
  type: COMPOSER_TYPES;
  isOpen: boolean;
  toggleMode?: () => void;
  closeComposer?: () => void;
  body: React.ReactNode;
  mode?: COMPOSER_MODES;
  showHeader?: boolean;
  title?: string;
  className?: string;
  showCheckbox?: boolean;
  onClickCheckbox?: (val: boolean) => void;
  checkboxValue?: boolean;
}

const composerClasses = cva(['fixed z-50 w-full'], {
  variants: {
    mode: {
      [COMPOSER_MODES.popover]: ['bottom-0 right-0 max-w-[600px]'],
      [COMPOSER_MODES.modal]: [
        'inset-0 grid place-items-center backdrop-blur-sm bg-[#e8ecf799.6]',
        '!max-w-none',
      ],
    },
    type: {
      [COMPOSER_TYPES.deal]: ['py-0 px-0 max-w-[480px]'],
      [COMPOSER_TYPES.call]: ['max-w-[470px]'],
      [COMPOSER_TYPES.email]: [],
      [COMPOSER_TYPES.sms]: [],
      [COMPOSER_TYPES.event]: [],
      [COMPOSER_TYPES.note]: [],
      [COMPOSER_TYPES.task]: ['py-0 px-0 max-w-[480px]'],
      [COMPOSER_TYPES.field]: [],
    },
    isAdmin: {
      true: 'mt-7',
      false: '',
    },
    isMinimized: {
      true: '!bottom-0 !right-0 !w-full',
      false: '',
    },
    isOpen: {
      true: '',
      false: 'hidden',
    },
  },
  defaultVariants: {
    mode: COMPOSER_MODES.popover,
    type: COMPOSER_TYPES.note,
    isAdmin: false,
    isMinimized: false,
    isOpen: false,
  },
});

const panelClasses = cva(['h-max', 'w-full', 'shadow-leapify-xl'], {
  variants: {
    mode: {
      [COMPOSER_MODES.modal]: '',
      [COMPOSER_MODES.popover]: 'max-w-[1000px]',
    },
    type: {
      [COMPOSER_TYPES.task]: 'max-w-[700px]',
      [COMPOSER_TYPES.deal]: 'max-w-[700px]',
      [COMPOSER_TYPES.call]: ['max-w-[470px]'],
      [COMPOSER_TYPES.email]: ['max-w-[800px]'],
      [COMPOSER_TYPES.sms]: ['max-w-[800px]'],
      [COMPOSER_TYPES.event]: ['max-w-[800px]'],
      [COMPOSER_TYPES.note]: ['max-w-[800px]'],
      [COMPOSER_TYPES.field]: ['max-w-[800px]'],
    },
  },
  defaultVariants: {},
});

const ComposerPopover: FC<PROPS> = ({
  type,
  isOpen,
  toggleMode = () => {},
  closeComposer = () => {},
  body,
  mode = COMPOSER_MODES.popover,
  showHeader = true,
  title,
  className,
  showCheckbox,
}) => {
  const { isAdmin } = useAdmin();
  const [minimized, setMinimized] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const useContext = useCallback(() => {
    switch (type) {
      case COMPOSER_TYPES.task:
        return useTasksContext;

      default:
        return () => {};
    }
  }, [type]);

  const context: any = useContext()();

  const { data: task } = useGetTask(context?.selectedTaskId);
  const { mutate: updateStatus } = useUpdateTaskStatus();

  const handleClickCheckbox = () => {
    switch (type) {
      case COMPOSER_TYPES.task:
        updateStatus({
          id: task?._id ?? '',
          status: isChecked ? 'pending' : 'completed',
        });
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (task?.status === 'COMPLETED') {
      setIsChecked(true);
    } else {
      setIsChecked(false);
    }
  }, [task]);

  const toggleMinimize = () => {
    setMinimized((prevVal) => !prevVal);
  };

  const _toggleMode = () => {
    if (minimized) {
      setMinimized(false);
    }
    toggleMode();
  };

  return (
    <Popover
      className={cn(
        composerClasses({
          type,
          isAdmin,
          isMinimized: minimized,
          mode,
          isOpen,
        }),
        mode === 'popover' && className,
      )}
    >
      {() => (
        <Popover.Panel static className={cn(panelClasses({ type, mode }))}>
          <div
            className={cn(
              'border-b-[0.5px] border-separator-primary',
              'flex h-10 items-center px-4',
              !showHeader
                ? 'bg-fill-tertiary justify-end'
                : 'bg-fill-primary justify-between',
              mode === COMPOSER_MODES.modal && 'rounded-t-[6px]',
            )}
          >
            {showHeader && (
              <div className="flex items-center gap-2">
                {showCheckbox ? (
                  <div className="flex items-center">
                    <MarkAsComplete
                      isComplete={task?.status === 'COMPLETED'}
                      handleToggle={() => {
                        handleClickCheckbox();
                        setIsChecked((prev) => !prev);
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <VscChevronDown
                      type="button"
                      role="button"
                      onClick={toggleMinimize}
                      className={cn(
                        'w-[14px]',
                        'h-[14px]',
                        'transform transition duration-100  ease-in',
                        minimized && 'rotate-180',
                        !minimized && 'rotate-0',
                      )}
                    />
                    <div
                      className={cn(
                        'font-semibold',
                        'text-sm',
                        'leading-[22px]',
                        'tracking-[0.04em]',
                        'capitalize',
                      )}
                    >
                      {title ?? type}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="flex gap-[17px]">
              {mode === COMPOSER_MODES.popover && (
                <FaExpandAlt
                  type="button"
                  role="button"
                  onClick={_toggleMode}
                  className="w-4 h-4"
                />
              )}
              {mode === COMPOSER_MODES.modal && (
                <FaCompressAlt
                  type="button"
                  role="button"
                  onClick={_toggleMode}
                  className="w-4 h-4"
                />
              )}
              <MdClose
                type="button"
                role="button"
                onClick={closeComposer}
                className="w-5 h-5 transition hover:text-danger-base"
              />
            </div>
          </div>
          {!minimized && (
            <div
              className={cn(
                'bg-fill-tertiary shadow-leapify-xl',
                mode === COMPOSER_MODES.modal && 'rounded-b-[6px]',
              )}
            >
              {body}
            </div>
          )}
        </Popover.Panel>
      )}
    </Popover>
  );
};

export default ComposerPopover;
